import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  UserPlus,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Store,
  Sparkles,
  Shield,
  Lock,
  Key,
  CheckCircle2,
  Heart,
  Star,
  Gift,
  Award,
} from "lucide-react";
import {
  validateEmail,
  validateName,
  validatePassword,
} from "@/lib/validation";

/**
 * Página de registro para nuevos usuarios
 *
 * Permite a los apoderados registrarse en el sistema
 * con validación de campos y feedback visual.
 * Optimizada para dispositivos móviles.
 */
const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { register, isLoading, error, clearError } = useAuth();

  const [formData, setFormData] = useState({
    rut: "",
    name: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // Obtener mensaje de éxito del estado de navegación
  useEffect(() => {
    if (location.state?.message) {
      setIsSuccess(true);
      // Limpiar el estado para evitar que se muestre en futuras navegaciones
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  /**
   * Sanitiza el RUT para aceptar múltiples formatos
   */
  const sanitizeRut = (rut: string): string => {
    // Remover todos los caracteres excepto números y 'k'
    let cleanRut = rut.replace(/[^0-9kK]/g, '');
    
    // Convertir 'k' a mayúscula
    cleanRut = cleanRut.toUpperCase();
    
    // Si tiene más de 1 caracter, separar el dígito verificador
    if (cleanRut.length > 1) {
      const body = cleanRut.slice(0, -1);
      const dv = cleanRut.slice(-1);
      
      // Formatear con puntos y guión
      let formattedRut = '';
      for (let i = body.length - 1, j = 0; i >= 0; i--, j++) {
        if (j > 0 && j % 3 === 0) {
          formattedRut = '.' + formattedRut;
        }
        formattedRut = body[i] + formattedRut;
      }
      
      return formattedRut + '-' + dv;
    }
    
    return cleanRut;
  };

  /**
   * Valida formato de RUT chileno
   */
  const validateRut = (rut: string): boolean => {
    // Limpiar RUT de puntos y guión
    const cleanRut = rut.replace(/[.-]/g, '');
    
    // Verificar longitud mínima
    if (cleanRut.length < 8) return false;
    
    // Verificar que solo contenga números y una letra al final
    if (!/^[0-9]+[0-9kK]$/.test(cleanRut)) return false;
    
    // Verificar dígito verificador
    const body = cleanRut.slice(0, -1);
    const dv = cleanRut.slice(-1).toUpperCase();
    
    let sum = 0;
    let multiplier = 2;
    
    for (let i = body.length - 1; i >= 0; i--) {
      sum += parseInt(body[i]) * multiplier;
      multiplier = multiplier === 7 ? 2 : multiplier + 1;
    }
    
    const expectedDv = 11 - (sum % 11);
    const calculatedDv = expectedDv === 11 ? '0' : expectedDv === 10 ? 'K' : expectedDv.toString();
    
    return dv === calculatedDv;
  };

     /**
    * Procesa el mensaje de error del servidor para hacerlo más descriptivo
    */
   const processServerError = (error: any): string => {
     // Si el error es un string directo
     if (typeof error === 'string') {
       const serverMessage = error;
       
       // Mapear mensajes específicos del servidor sin revelar información sensible
       if (serverMessage.includes('User with this email already exists') || 
           serverMessage.includes('email already exists') ||
           serverMessage.includes('duplicate key') && serverMessage.includes('email')) {
         return "No se pudo completar el registro. Verifica que la información sea correcta.";
       }
       
       if (serverMessage.includes('User with this RUT already exists') || 
           serverMessage.includes('RUT already exists') ||
           serverMessage.includes('duplicate key') && serverMessage.includes('rut')) {
         return "El RUT ingresado ya está registrado en el sistema. Verifica el número o inicia sesión si ya tienes cuenta.";
       }
       
       if (serverMessage.includes('All fields are required')) {
         return "Todos los campos son requeridos. Completa toda la información solicitada.";
       }
       
       if (serverMessage.includes('Password must be at least 6 characters')) {
         return "La contraseña debe tener al menos 6 caracteres.";
       }
       
       return "No se pudo completar el registro. Verifica la información e intenta nuevamente.";
     }
     
     // Si es un error de respuesta del servidor
     if (error.response?.data?.message) {
       const serverMessage = error.response.data.message;
       
       // Mapear mensajes específicos del servidor sin revelar información sensible
       if (serverMessage.includes('User with this email already exists') || 
           serverMessage.includes('email already exists') ||
           serverMessage.includes('duplicate key') && serverMessage.includes('email')) {
         return "No se pudo completar el registro. Verifica que la información sea correcta.";
       }
       
       if (serverMessage.includes('User with this RUT already exists') || 
           serverMessage.includes('RUT already exists') ||
           serverMessage.includes('duplicate key') && serverMessage.includes('rut')) {
         return "El RUT ingresado ya está registrado en el sistema. Verifica el número o inicia sesión si ya tienes cuenta.";
       }
       
       if (serverMessage.includes('All fields are required')) {
         return "Todos los campos son requeridos. Completa toda la información solicitada.";
       }
       
       if (serverMessage.includes('Password must be at least 6 characters')) {
         return "La contraseña debe tener al menos 6 caracteres.";
       }
       
       return "No se pudo completar el registro. Verifica la información e intenta nuevamente.";
     }
     
     // Si es un error de red
     if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
       return "Error de conexión. Verifica tu conexión a internet e intenta nuevamente.";
     }
     
     // Si es un error 400 (Bad Request)
     if (error.response?.status === 400) {
       return "Los datos ingresados no son válidos. Revisa la información e intenta nuevamente.";
     }
     
     // Si es un error 409 (Conflict) - duplicados
     if (error.response?.status === 409) {
       return "No se pudo completar el registro. Verifica que la información sea correcta.";
     }
     
     // Si es un error 500 (Server Error)
     if (error.response?.status === 500) {
       return "Error interno del servidor. Intenta nuevamente en unos momentos.";
     }
     
     // Mensaje por defecto
     return "Error al registrar cuenta. Intenta nuevamente.";
   };

  /**
   * Maneja los cambios en los campos del formulario
   */
  const handleInputChange = (field: string, value: string) => {
    if (error) {
      clearError();
    }

    // Limpiar error de validación del campo
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }

    // Sanitizar RUT si es necesario
    if (field === "rut") {
      value = sanitizeRut(value);
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Valida un campo específico
   */
  const validateField = (field: string, value: string) => {
    let error = "";

    switch (field) {
      case "rut":
        if (!value) {
          error = "El RUT es requerido";
        } else if (!validateRut(value)) {
          error = "El RUT ingresado no es válido";
        }
        break;
      case "name":
        if (!value) {
          error = "El nombre es requerido";
        } else if (!validateName(value)) {
          error = "El nombre debe tener al menos 2 caracteres";
        }
        break;
      case "lastName":
        if (!value) {
          error = "El apellido es requerido";
        } else if (!validateName(value)) {
          error = "El apellido debe tener al menos 2 caracteres";
        }
        break;
      case "email":
        if (!value) {
          error = "El email es requerido";
        } else if (!validateEmail(value)) {
          error = "Formato de email inválido";
        }
        break;
      case "password":
        if (!value) {
          error = "La contraseña es requerida";
        } else if (!validatePassword(value)) {
          error = "La contraseña debe tener al menos 6 caracteres";
        }
        break;
      case "confirmPassword":
        if (!value) {
          error = "Confirma tu contraseña";
        } else if (value !== formData.password) {
          error = "Las contraseñas no coinciden";
        }
        break;
    }

    return error;
  };

  /**
   * Maneja el envío del formulario de registro
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isLoading) return;

    // Validar todos los campos
    const errors: { [key: string]: string } = {};
    Object.keys(formData).forEach((field) => {
      const error = validateField(
        field,
        formData[field as keyof typeof formData]
      );
      if (error) {
        errors[field] = error;
      }
    });

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Sanitizar RUT para envío al servidor con formato xxxxxxxx-x
    const cleanRut = formData.rut.replace(/[.-]/g, '');
    const formattedRut = cleanRut.slice(0, -1) + '-' + cleanRut.slice(-1);

    const success = await register({
      nombre: formData.name,
      apellido: formData.lastName,
      email: formData.email,
      password: formData.password,
      rut: formattedRut,
    });

    if (success) {
      setIsSuccess(true);
    }
  };

  // Página de éxito
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-sm mx-auto">
          <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
            <CardHeader className="text-center space-y-1 pb-3">
              <div className="flex justify-center mb-2">
                <div className="p-1.5 bg-gradient-to-br from-green-500/20 to-green-500/10 rounded-lg border border-green-500/20">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-lg font-bold text-foreground">
                ¡Registro Exitoso!
              </CardTitle>
              <CardDescription className="text-xs text-muted-foreground">
                Tu cuenta ha sido creada correctamente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="text-center space-y-2">
                <p className="text-xs text-muted-foreground">
                  Ya puedes iniciar sesión con tus credenciales
                </p>
                <Button
                  onClick={() => navigate("/login")}
                  className="w-full h-8 text-xs font-medium rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                >
                  Ir al Login
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Contenido principal - Layout de 2 columnas */}
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-4xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            {/* Columna Izquierda - Formulario */}
            <div className="flex flex-col justify-center">
              {/* Hero Section */}
              <div className="text-center lg:text-left mb-4">
                <Badge className="bg-primary/20 text-primary border-primary/30 px-2 py-1 rounded-full mb-2 text-xs">
                  <Sparkles className="h-2.5 w-2.5 mr-1" />
                  Sistema de Gestión Escolar
                </Badge>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Únete a Nosotros
                </h1>
                <p className="text-sm text-muted-foreground">
                  Crea tu cuenta para acceder a todos los servicios escolares
                  disponibles
                </p>
              </div>

              {/* Card de Registro */}
              <Card className="shadow-lg hover:shadow-xl transition-all duration-300 border-0 bg-gradient-to-br from-card/80 to-card/60 backdrop-blur-xl">
                <CardHeader className="space-y-1 pb-3">
                  <div className="flex items-center justify-center lg:justify-start mb-2">
                    <div className="p-1.5 bg-gradient-to-br from-primary/20 to-primary/10 rounded-lg border border-primary/20">
                      <UserPlus className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <CardTitle className="text-lg font-bold text-center lg:text-left text-foreground">
                    Crear Cuenta
                  </CardTitle>
                  <CardDescription className="text-center lg:text-left text-muted-foreground text-xs">
                    Completa el formulario para registrarte
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3">
                  <form onSubmit={handleSubmit} className="space-y-3">
                    {/* Campo RUT */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="rut"
                        className="text-sm font-medium text-foreground"
                      >
                        RUT
                      </Label>
                      <Input
                        id="rut"
                        type="text"
                        placeholder="Ej: 12.345.678-9"
                        value={formData.rut}
                        onChange={(e) =>
                          handleInputChange("rut", e.target.value)
                        }
                        disabled={isLoading}
                        required
                        className={`h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                          validationErrors.rut ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                        }`}
                        autoComplete="username"
                      />
                      {validationErrors.rut && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.rut}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Acepta formatos: 12.345.678-9, 12345678-9, 12.345.678-9
                      </p>
                    </div>

                    {/* Campo Nombre */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="name"
                        className="text-sm font-medium text-foreground"
                      >
                        Nombre
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Tu nombre"
                        value={formData.name}
                        onChange={(e) =>
                          handleInputChange("name", e.target.value)
                        }
                        disabled={isLoading}
                        required
                        className={`h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                          validationErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                        }`}
                        autoComplete="given-name"
                      />
                      {validationErrors.name && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.name}
                        </p>
                      )}
                    </div>

                    {/* Campo Apellido */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="lastName"
                        className="text-sm font-medium text-foreground"
                      >
                        Apellido
                      </Label>
                      <Input
                        id="lastName"
                        type="text"
                        placeholder="Tu apellido"
                        value={formData.lastName}
                        onChange={(e) =>
                          handleInputChange("lastName", e.target.value)
                        }
                        disabled={isLoading}
                        required
                        className={`h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                          validationErrors.lastName ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                        }`}
                        autoComplete="family-name"
                      />
                      {validationErrors.lastName && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.lastName}
                        </p>
                      )}
                    </div>

                    {/* Campo Email */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="email"
                        className="text-sm font-medium text-foreground"
                      >
                        Email
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        disabled={isLoading}
                        required
                        className={`h-9 text-sm rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                          validationErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                        }`}
                        autoComplete="email"
                      />
                      {validationErrors.email && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.email}
                        </p>
                      )}
                    </div>

                    {/* Campo Contraseña */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="password"
                        className="text-sm font-medium text-foreground"
                      >
                        Contraseña
                      </Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Mínimo 6 caracteres"
                          value={formData.password}
                          onChange={(e) =>
                            handleInputChange("password", e.target.value)
                          }
                          disabled={isLoading}
                          required
                          className={`h-9 text-sm pr-9 rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                            validationErrors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                          }`}
                          autoComplete="new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-1.5 py-1 hover:bg-transparent focus:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                          aria-label={
                            showPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Eye className="h-3 w-3 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      {validationErrors.password && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.password}
                        </p>
                      )}
                    </div>

                    {/* Campo Confirmar Contraseña */}
                    <div className="space-y-1.5">
                      <Label
                        htmlFor="confirmPassword"
                        className="text-sm font-medium text-foreground"
                      >
                        Confirmar Contraseña
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Repite tu contraseña"
                          value={formData.confirmPassword}
                          onChange={(e) =>
                            handleInputChange("confirmPassword", e.target.value)
                          }
                          disabled={isLoading}
                          required
                          className={`h-9 text-sm pr-9 rounded-md border-2 focus:ring-2 focus:ring-primary/20 transition-all duration-200 ${
                            validationErrors.confirmPassword ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''
                          }`}
                          autoComplete="new-password"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-1.5 py-1 hover:bg-transparent focus:bg-transparent"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          disabled={isLoading}
                          aria-label={
                            showConfirmPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Eye className="h-3 w-3 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      {validationErrors.confirmPassword && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors.confirmPassword}
                        </p>
                      )}
                    </div>

                                         {/* Mensaje de Error */}
                     {error && (
                       <Alert
                         variant="destructive"
                         className="border-red-500/20 bg-red-500/10 rounded-xl"
                       >
                         <AlertCircle className="h-4 w-4" />
                         <AlertDescription className="text-sm">
                           {processServerError(error)}
                         </AlertDescription>
                       </Alert>
                     )}

                    {/* Botón de Registro */}
                    <Button
                      type="submit"
                      className="w-full h-9 text-xs font-medium rounded-md bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 transition-all duration-200 hover:scale-105 hover:shadow-lg"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                          Creando cuenta...
                        </>
                      ) : (
                        <>
                          <UserPlus className="mr-2 h-3 w-3" />
                          Crear Cuenta
                        </>
                      )}
                    </Button>
                  </form>

                  {/* Enlace de Login */}
                  <div className="text-center lg:text-left pt-1">
                    <p className="text-xs text-muted-foreground">
                      ¿Ya tienes una cuenta?{" "}
                      <Link
                        to="/login"
                        className="inline-flex items-center gap-1 text-primary hover:text-primary/80 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 rounded"
                      >
                        <Key className="h-3 w-3" />
                        Inicia sesión aquí
                      </Link>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Columna Derecha - Información de Ayuda */}
            <div className="flex flex-col justify-center space-y-3">
              {/* Sección de Beneficios */}
              <div className="space-y-3">
                <div className="text-center lg:text-left">
                  <h2 className="text-lg lg:text-xl font-bold text-foreground mb-1">
                    ¿Por qué registrarte?
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Descubre todos los beneficios disponibles
                  </p>
                </div>

                {/* Cards de Beneficios */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-2">
                  <div className="p-2 bg-gradient-to-r from-primary/10 to-primary/5 rounded-md border border-primary/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-primary/20 rounded-sm">
                        <Heart className="h-3 w-3 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Gestión Familiar
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Administra productos para toda tu familia
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-green-500/10 to-green-500/5 rounded-md border border-green-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-green-500/20 rounded-sm">
                        <Star className="h-3 w-3 text-green-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Control Total
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Supervisa y gestiona todos los productos
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-md border border-blue-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-blue-500/20 rounded-sm">
                        <Gift className="h-3 w-3 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Beneficios Exclusivos
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Acceso a ofertas y descuentos especiales
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 bg-gradient-to-r from-purple-500/10 to-purple-500/5 rounded-md border border-purple-500/20 hover:shadow-sm transition-all duration-200">
                    <div className="flex items-start gap-1.5">
                      <div className="p-1 bg-purple-500/20 rounded-sm">
                        <Award className="h-3 w-3 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-0.5 text-xs">
                          Experiencia Premium
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          Interfaz moderna y fácil de usar
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sección de Funcionalidades */}
              <div className="p-3 bg-gradient-to-r from-muted/50 to-muted/30 rounded-md border border-border/50">
                <h3 className="font-semibold text-xs text-foreground mb-2 flex items-center gap-1">
                  <Shield className="h-2.5 w-2.5 text-primary" />
                  🚀 Funcionalidades Detalladas
                </h3>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div className="flex items-start gap-1 p-1 bg-background/50 rounded-sm">
                    <CheckCircle2 className="h-2.5 w-2.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Gestión completa de productos escolares</span>
                  </div>
                  <div className="flex items-start gap-1 p-1 bg-background/50 rounded-sm">
                    <CheckCircle2 className="h-2.5 w-2.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Acceso a códigos QR para canjes</span>
                  </div>
                  <div className="flex items-start gap-1 p-1 bg-background/50 rounded-sm">
                    <CheckCircle2 className="h-2.5 w-2.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Historial detallado de transacciones</span>
                  </div>
                  <div className="flex items-start gap-1 p-1 bg-background/50 rounded-sm">
                    <CheckCircle2 className="h-2.5 w-2.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Notificaciones en tiempo real</span>
                  </div>
                  <div className="flex items-start gap-1 p-1 bg-background/50 rounded-sm">
                    <CheckCircle2 className="h-2.5 w-2.5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span>Soporte técnico especializado</span>
                  </div>
                </div>
              </div>

              {/* Sección de Seguridad */}
              <div className="p-2 bg-gradient-to-r from-amber-500/10 to-amber-500/5 rounded-md border border-amber-500/20">
                <h4 className="font-medium text-xs mb-1 text-amber-600 flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" />
                  Seguridad Garantizada
                </h4>
                <ul className="text-xs text-amber-600/80 space-y-0.5">
                  <li>• Encriptación de datos de alta seguridad</li>
                  <li>• Protección contra ataques informáticos</li>
                  <li>• Cumplimiento de estándares de privacidad</li>
                  <li>• Acceso restringido por roles</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botón de regreso al final */}
      <div className="p-4 sm:p-6">
        <Button
          variant="ghost"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 h-7 hover:bg-primary/10 transition-colors text-xs"
        >
          <ArrowLeft className="h-3 w-3" />
          <Store className="h-3 w-3" />
          Volver a la Tienda
        </Button>
      </div>
    </div>
  );
};

export default RegisterPage;
